import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Activity, Focus, HandHelping, Palette, Moon,
  Search, Clock, Users, Plus, X, Check, ChevronLeft, ChevronRight,
  LayoutGrid, GalleryHorizontal, Rows3, Flame, Lock,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useApp } from "@/store/AppContext";
import { useAuth } from "@/store/AuthContext";
import {
  useCategories, useLibrary, useCategoriesMap, useGroupMembers,
  useGroupCalendar,
} from "@/lib/hooks";
import HabitCard from "@/components/HabitCard";
import AdSlot from "@/components/AdSlot";
import LibraryViewPicker, { VIEW_OPTIONS } from "@/components/LibraryViewPicker";
import CreateHabitDialog from "@/components/CreateHabitDialog";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink,
  PaginationPrevious, PaginationNext, PaginationEllipsis,
} from "@/components/ui/pagination";

const CATEGORY_ICON = {
  mind: Sparkles,
  body: Activity,
  focus: Focus,
  faith: HandHelping,
  craft: Palette,
  rest: Moon,
};

const VIEW_STORAGE_KEY = "cadence.libraryView";
const PAGE_SIZE = 6;

function pageRange(current, total) {
  // Build a compact page list: 1 … c-1 c c+1 … total
  const pages = new Set([1, total, current, current - 1, current + 1]);
  return [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
}

export default function HabitLibrary() {
  const { scheduled, addHabit, removeHabit } = useApp();
  const { user } = useAuth();
  const isPremium = !!user?.is_premium;
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const { data: categories = [] } = useCategories();
  const { data: habits = [], isLoading } = useLibrary({ category: active, q: query });
  const catMap = useCategoriesMap();
  const { data: members = [] } = useGroupMembers();
  const { data: calendar = [] } = useGroupCalendar();

  // ---- View mode: read from localStorage, prompt first-time user ----
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_STORAGE_KEY); } catch { return null; }
  });
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (view == null) setPickerOpen(true);
  }, [view]);

  const persistView = (id) => {
    try { localStorage.setItem(VIEW_STORAGE_KEY, id); } catch { /* noop */ }
    setView(id);
    setPickerOpen(false);
    setPage(1);
  };

  // Reset page whenever filter/search changes
  useEffect(() => { setPage(1); }, [active, query, view]);

  const isAdded = (id) => scheduled.some((s) => s.template_id === id);

  const onAdd = (h) => {
    addHabit(h.id);
    toast("Added to your rhythm", {
      description: `${h.title} · ${h.durationMin} min at ${h.defaultStart}`,
    });
  };
  const onRemove = (h) => {
    removeHabit(h.id);
    toast("Removed from your list", {
      description: h.title,
      action: { label: "Undo", onClick: () => addHabit(h.id) },
    });
  };

  // ---- Circle-member overlap per habit (for Carousel view) ----
  // For each habit template, find the circle members who have any calendar entry
  // for that habit. Their `overlapPct` doubles as "consistency" for the demo.
  const habitOverlap = useMemo(() => {
    const map = {};
    const memberById = Object.fromEntries(members.map((m) => [m.id, m]));
    for (const entry of calendar) {
      const m = memberById[entry.memberId];
      if (!m) continue;
      const list = (map[entry.habitId] ||= []);
      if (!list.some((x) => x.id === m.id)) list.push(m);
    }
    return map;
  }, [calendar, members]);

  // ---- Paged habits for Grid/List ----
  const totalPages = Math.max(1, Math.ceil(habits.length / PAGE_SIZE));
  const paged = habits.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const activeView = view || "grid";

  return (
    <div className="space-y-8" data-testid="library-page">
      <LibraryViewPicker open={pickerOpen} onPick={persistView} />

      <header className="max-w-3xl">
        <span className="eyebrow">The library</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-stone-900 mt-2 leading-[1.05]">
          Small, timed things. <span className="italic text-terracotta">Kept together.</span>
        </h1>
        <p className="text-stone-600 mt-4 leading-relaxed">
          A curated set of habits with real start-to-finish intervals. Pick a few. Cadence quietly assembles the circle whose rhythm most resembles yours.
        </p>

        {/* Premium: start your own habit */}
        <div className="mt-6" data-testid="library-custom-habit-cta">
          {isPremium ? (
            <button
              onClick={() => setCreateOpen(true)}
              className="btn-accent"
              data-testid="library-start-custom-habit"
            >
              <Plus size={16} /> Start your own habit
              <span className="ml-2 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-amber-100 text-amber-900">
                <Sparkles size={9} /> Premium
              </span>
            </button>
          ) : (
            <Link
              to="/premium"
              className="btn-outline"
              data-testid="library-start-custom-habit-locked"
            >
              <Lock size={14} /> Start your own habit — upgrade to Premium
            </Link>
          )}
        </div>
      </header>

      <CreateHabitDialog open={createOpen} onOpenChange={setCreateOpen} />

      {/* View toggle */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="library-toolbar">
        <div className="inline-flex items-center gap-1 rounded-full border border-stone-200 p-1 bg-white/60 backdrop-blur-sm" data-testid="library-view-toggle" role="tablist">
          {VIEW_OPTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={activeView === id}
              onClick={() => persistView(id)}
              data-testid={`library-view-${id}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                activeView === id
                  ? "bg-charcoal text-paper"
                  : "text-stone-600 hover:bg-stone-100"
              }`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-72">
          <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search habits…"
            data-testid="library-search"
            className="w-full bg-transparent border-b-2 border-stone-300 focus:border-terracotta outline-none py-2 pl-6 text-sm placeholder:text-stone-400"
          />
        </div>
      </div>

      {/* Filter chips with icons */}
      <div className="flex flex-wrap gap-2" data-testid="category-filters">
        <button
          onClick={() => setActive("all")}
          data-testid="cat-all"
          className={`tag-pill inline-flex items-center gap-1.5 ${active === "all" ? "bg-charcoal text-paper border-charcoal" : ""}`}
        >
          <LayoutGrid size={12} /> All
        </button>
        {categories.map((c) => {
          const Icon = CATEGORY_ICON[c.id] || Sparkles;
          const isActive = active === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              data-testid={`cat-${c.id}`}
              className={`tag-pill inline-flex items-center gap-1.5 ${isActive ? "bg-charcoal text-paper border-charcoal" : ""}`}
            >
              <Icon size={12} /> {c.label}
            </button>
          );
        })}
      </div>

      {isLoading && <p className="text-sm text-stone-500">Loading library…</p>}
      {!isLoading && habits.length === 0 && (
        <p className="text-sm text-stone-500" data-testid="library-empty">No habits match this filter.</p>
      )}

      {/* --- Views --- */}
      {!isLoading && habits.length > 0 && activeView === "grid" && (
        <GridView
          habits={paged}
          catMap={catMap}
          isAdded={isAdded}
          onAdd={onAdd}
          onRemove={onRemove}
        />
      )}

      {!isLoading && habits.length > 0 && activeView === "list" && (
        <ListView
          habits={paged}
          catMap={catMap}
          isAdded={isAdded}
          onAdd={onAdd}
          onRemove={onRemove}
        />
      )}

      {!isLoading && habits.length > 0 && activeView === "carousel" && (
        <CarouselView
          allHabits={habits}
          categories={categories}
          activeCategory={active}
          catMap={catMap}
          habitOverlap={habitOverlap}
          isAdded={isAdded}
          onAdd={onAdd}
          onRemove={onRemove}
        />
      )}

      {/* Pagination for Grid + List only */}
      {(activeView === "grid" || activeView === "list") && habits.length > PAGE_SIZE && (
        <Pagination data-testid="library-pagination" className="pt-2">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => { e.preventDefault(); setPage((p) => Math.max(1, p - 1)); }}
                aria-disabled={page === 1}
                className={page === 1 ? "pointer-events-none opacity-40" : ""}
                data-testid="library-pagination-prev"
              />
            </PaginationItem>
            {pageRange(page, totalPages).map((p, idx, arr) => {
              const prev = arr[idx - 1];
              const showGap = prev != null && p - prev > 1;
              return (
                <span key={p} className="contents">
                  {showGap && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      isActive={p === page}
                      onClick={(e) => { e.preventDefault(); setPage(p); }}
                      data-testid={`library-pagination-page-${p}`}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                </span>
              );
            })}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => { e.preventDefault(); setPage((p) => Math.min(totalPages, p + 1)); }}
                aria-disabled={page === totalPages}
                className={page === totalPages ? "pointer-events-none opacity-40" : ""}
                data-testid="library-pagination-next"
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <div className="pt-4">
        <AdSlot variant="inline" data-testid="library-inline-ad" />
      </div>
    </div>
  );
}

// ============================================================
function GridView({ habits, catMap, isAdded, onAdd, onRemove }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="habit-grid">
      {habits.map((h) => (
        <HabitCard
          key={h.id}
          habit={h}
          categoryLabel={catMap[h.category] || h.category}
          added={isAdded(h.id)}
          onAdd={() => onAdd(h)}
          onRemove={() => onRemove(h)}
          onOpen={() => {}}
        />
      ))}
    </div>
  );
}

// ============================================================
function ListView({ habits, catMap, isAdded, onAdd, onRemove }) {
  return (
    <div className="card-editorial overflow-hidden" data-testid="habit-list">
      <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-stone-100 text-[10px] uppercase tracking-widest text-stone-500 font-medium">
        <div className="col-span-5">Habit</div>
        <div className="col-span-2">Category</div>
        <div className="col-span-1">Length</div>
        <div className="col-span-2">Default</div>
        <div className="col-span-2 text-right">Action</div>
      </div>
      {habits.map((h) => {
        const Icon = CATEGORY_ICON[h.category] || Sparkles;
        const added = isAdded(h.id);
        return (
          <div
            key={h.id}
            className="grid grid-cols-12 gap-3 md:gap-4 items-center px-5 py-4 border-b border-stone-100 hover:bg-stone-50/60"
            data-testid={`habit-row-${h.id}`}
          >
            <div className="col-span-12 md:col-span-5 flex items-start gap-3 min-w-0">
              <span className="w-9 h-9 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center shrink-0">
                <Icon size={14} />
              </span>
              <div className="min-w-0">
                <p className="font-serif text-lg text-stone-900 leading-tight truncate">{h.title}</p>
                <p className="text-xs text-stone-500 mt-0.5 line-clamp-2 md:line-clamp-1">{h.description}</p>
              </div>
            </div>
            <div className="col-span-4 md:col-span-2 text-xs text-stone-600 flex items-center gap-1.5">
              <Icon size={12} className="md:hidden text-terracotta" />
              {catMap[h.category] || h.category}
            </div>
            <div className="col-span-3 md:col-span-1 data-mono text-xs text-stone-700">{h.durationMin}m</div>
            <div className="col-span-5 md:col-span-2 text-xs text-stone-600 flex items-center gap-1.5">
              <Clock size={12} /> {h.defaultStart}
              <span className="hidden md:inline-flex items-center gap-1 ml-1 text-stone-400">
                · <Users size={11} /> {h.members}
              </span>
            </div>
            <div className="col-span-12 md:col-span-2 flex md:justify-end">
              {added ? (
                <button
                  className="btn-outline text-xs !min-h-[2rem] !py-1 hover:!bg-red-50 hover:!text-red-700 hover:!border-red-300"
                  onClick={() => onRemove(h)}
                  data-testid={`habit-row-remove-${h.id}`}
                >
                  <X size={13} /> Remove
                </button>
              ) : (
                <button
                  className="btn-accent text-xs !min-h-[2rem] !py-1"
                  onClick={() => onAdd(h)}
                  data-testid={`habit-row-add-${h.id}`}
                >
                  <Plus size={13} /> Add
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
function CarouselView({ allHabits, categories, activeCategory, catMap, habitOverlap, isAdded, onAdd, onRemove }) {
  // Group habits by category; if the user has filtered to one category we still
  // show that single row (respecting the filter).
  const groups = useMemo(() => {
    const byCat = {};
    for (const h of allHabits) (byCat[h.category] ||= []).push(h);
    const order = activeCategory === "all"
      ? categories.map((c) => c.id)
      : [activeCategory];
    return order
      .filter((cid) => byCat[cid]?.length)
      .map((cid) => ({ id: cid, label: catMap[cid] || cid, items: byCat[cid] }));
  }, [allHabits, categories, activeCategory, catMap]);

  return (
    <div className="space-y-10" data-testid="habit-carousel">
      {groups.map((g) => {
        const Icon = CATEGORY_ICON[g.id] || Sparkles;
        return (
          <section key={g.id} data-testid={`habit-carousel-cat-${g.id}`}>
            <div className="flex items-baseline justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center">
                  <Icon size={14} />
                </span>
                <h2 className="font-serif text-2xl text-stone-900">{g.label}</h2>
                <span className="data-mono text-xs text-stone-500">· {g.items.length}</span>
              </div>
            </div>
            <div
              className="flex gap-5 overflow-x-auto pb-4 -mx-5 px-5 snap-x snap-mandatory scroll-smooth"
              style={{ scrollbarWidth: "thin" }}
              data-testid={`habit-carousel-track-${g.id}`}
            >
              {g.items.map((h) => (
                <CarouselCard
                  key={h.id}
                  habit={h}
                  categoryLabel={catMap[h.category] || h.category}
                  added={isAdded(h.id)}
                  onAdd={() => onAdd(h)}
                  onRemove={() => onRemove(h)}
                  overlapMembers={habitOverlap[h.id] || []}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function CarouselCard({ habit, categoryLabel, added, onAdd, onRemove, overlapMembers }) {
  const Icon = CATEGORY_ICON[habit.category] || Sparkles;
  const previewMembers = overlapMembers.slice(0, 3);
  const avgConsistency = previewMembers.length
    ? Math.round(previewMembers.reduce((s, m) => s + (m.overlapPct || 0), 0) / previewMembers.length)
    : null;
  return (
    <article
      className="snap-start shrink-0 w-[300px] sm:w-[340px] card-editorial p-6 flex flex-col gap-4 hover:-translate-y-0.5 transition-transform duration-200"
      data-testid={`habit-carousel-card-${habit.id}`}
    >
      <div className="flex items-center justify-between">
        <span className="tag-pill inline-flex items-center gap-1.5">
          <Icon size={12} /> {categoryLabel}
        </span>
        <span className="data-mono">{habit.durationMin}m</span>
      </div>
      <div>
        <h3 className="font-serif text-2xl text-stone-900 leading-tight">{habit.title}</h3>
        <p className="text-sm text-stone-600 mt-1.5 leading-relaxed line-clamp-3">{habit.description}</p>
      </div>

      {/* Circle members who keep this habit + their consistency */}
      <div className="rounded-2xl bg-sage/10 border border-sage/20 p-3" data-testid={`habit-carousel-overlap-${habit.id}`}>
        <div className="flex items-center justify-between">
          <span className="eyebrow flex items-center gap-1.5"><Users size={11} /> In your circle</span>
          {avgConsistency != null && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest bg-sage/25 text-stone-800"
              title="Average consistency of overlapping members"
              data-testid={`habit-carousel-consistency-${habit.id}`}
            >
              <Flame size={10} /> {avgConsistency}% consistent
            </span>
          )}
        </div>
        {previewMembers.length ? (
          <ul className="mt-3 space-y-2">
            {previewMembers.map((m) => (
              <li key={m.id} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <img
                    src={m.avatar}
                    alt={m.name}
                    className="w-6 h-6 rounded-full object-cover border border-white"
                    referrerPolicy="no-referrer"
                  />
                  <span className="text-stone-800 truncate">{m.name}</span>
                </div>
                <span className="data-mono text-stone-500">{m.overlapPct}%</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-stone-500 mt-2">
            No one in your circle keeps this yet — you could be first.
          </p>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-stone-500 font-sans mt-auto">
        <span className="inline-flex items-center gap-1.5"><Clock size={13} /> default {habit.defaultStart}</span>
        <span className="inline-flex items-center gap-1.5"><Users size={13} /> {habit.members} keep this</span>
      </div>
      <div className="pt-2 border-t border-stone-100">
        {added ? (
          <button
            className="btn-outline w-full hover:!bg-red-50 hover:!text-red-700 hover:!border-red-300"
            onClick={onRemove}
            data-testid={`habit-carousel-remove-${habit.id}`}
          >
            <Check size={16} /> On your list · remove
          </button>
        ) : (
          <button className="btn-accent w-full" onClick={onAdd} data-testid={`habit-carousel-add-${habit.id}`}>
            <Plus size={16} /> Add to my rhythm
          </button>
        )}
      </div>
    </article>
  );
}
